import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart3, TrendingUp, MapPin, Clock, Package, Cpu } from 'lucide-react';

interface SmartAnalyticsData {
  topProducts: Array<{
    productName: string;
    interactionCount: number;
    percentage: number;
    trend: number;
  }>;
  machinePerformance: Array<{
    machineName: string;
    totalInteractions: number;
    uptime: number;
    avgInteractionsPerDay: number;
    status: 'high' | 'medium' | 'low';
  }>;
  geographicDistribution: Array<{
    location: string;
    interactionCount: number;
    machineCount: number;
    avgInteractionsPerMachine: number;
  }>;
  timeAnalytics: {
    peakHours: Array<{ hour: number; count: number }>;
    dailyPattern: Array<{ day: string; count: number }>;
    hourlyDistribution: Array<{ hour: number; count: number }>;
  };
  inferredMetrics: {
    totalInteractions: number;
    uniqueProducts: number;
    uniqueMachines: number;
    avgInteractionsPerDay: number;
    topPerformingLocation: string;
    mostActiveHour: number;
  };
}

export default function SmartAnalytics() {
  const [analytics, setAnalytics] = useState<SmartAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [availableTables, setAvailableTables] = useState<Array<{ tableName: string; rowCount: number; createdAt: string }>>([]);

  useEffect(() => {
    fetchAvailableTables();
  }, []);

  useEffect(() => {
    if (selectedTable) {
      fetchAnalytics();
    }
  }, [selectedTable]);

  const fetchAvailableTables = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/data/tables', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableTables(data.tables || []);
        if (data.tables && data.tables.length > 0) {
          setSelectedTable(data.tables[0].tableName);
        }
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  };

  const fetchAnalytics = async () => {
    if (!selectedTable) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/analytics/smart-analytics/${selectedTable}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        console.error('Failed to fetch analytics');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'high': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatHour = (hour: number) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Analyzing your data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Smart Analytics Dashboard</h1>
          <p className="text-gray-600">
            AI-powered insights from your uploaded data
          </p>
        </div>

        {/* Table Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Select Data Source</CardTitle>
            <CardDescription>Choose which uploaded dataset to analyze</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedTable} onValueChange={setSelectedTable}>
              <SelectTrigger className="w-[400px]">
                <SelectValue placeholder="Select a table" />
              </SelectTrigger>
              <SelectContent>
                {availableTables.map((table) => (
                  <SelectItem key={table.tableName} value={table.tableName}>
                    {table.tableName} ({table.rowCount} rows)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={fetchAnalytics} className="mt-4">
              Refresh Analytics
            </Button>
          </CardContent>
        </Card>

        {analytics && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <BarChart3 className="h-8 w-8 text-blue-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Interactions</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.inferredMetrics.totalInteractions.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <Package className="h-8 w-8 text-green-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Unique Products</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.inferredMetrics.uniqueProducts}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <Cpu className="h-8 w-8 text-purple-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Active Machines</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.inferredMetrics.uniqueMachines}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <TrendingUp className="h-8 w-8 text-orange-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Avg Daily Interactions</p>
                      <p className="text-2xl font-bold text-gray-900">{analytics.inferredMetrics.avgInteractionsPerDay}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Top Products */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Package className="h-5 w-5 mr-2" />
                    Top Performing Products
                  </CardTitle>
                  <CardDescription>Products ranked by interaction count</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analytics.topProducts.slice(0, 5).map((product, index) => (
                      <div key={product.productName} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium mr-3">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{product.productName}</p>
                            <p className="text-sm text-gray-500">{product.percentage}% of total</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{product.interactionCount}</p>
                          <p className={`text-sm ${product.trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {product.trend > 0 ? '+' : ''}{product.trend.toFixed(1)}%
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Machine Performance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Cpu className="h-5 w-5 mr-2" />
                    Machine Performance
                  </CardTitle>
                  <CardDescription>Machines ranked by activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analytics.machinePerformance.slice(0, 5).map((machine, index) => (
                      <div key={machine.machineName} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-medium mr-3">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{machine.machineName}</p>
                            <p className="text-sm text-gray-500">{machine.uptime.toFixed(1)}% uptime</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{machine.totalInteractions}</p>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(machine.status)}`}>
                            {machine.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Geographic Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <MapPin className="h-5 w-5 mr-2" />
                    Geographic Performance
                  </CardTitle>
                  <CardDescription>Activity by location</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analytics.geographicDistribution.slice(0, 5).map((location, index) => (
                      <div key={location.location} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-medium mr-3">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{location.location}</p>
                            <p className="text-sm text-gray-500">{location.machineCount} machines</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{location.interactionCount}</p>
                          <p className="text-sm text-gray-500">{location.avgInteractionsPerMachine}/machine</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Time Analytics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Clock className="h-5 w-5 mr-2" />
                    Peak Activity Hours
                  </CardTitle>
                  <CardDescription>Most active times of day</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analytics.timeAnalytics.peakHours.map((hour, index) => (
                      <div key={hour.hour} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-medium mr-3">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{formatHour(hour.hour)}</p>
                            <p className="text-sm text-gray-500">Peak hour</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{hour.count}</p>
                          <p className="text-sm text-gray-500">interactions</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Insights Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Key Insights</CardTitle>
                <CardDescription>AI-generated insights from your data</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">Performance Highlights</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>• <strong>{analytics.inferredMetrics.topPerformingLocation}</strong> is your top-performing location</li>
                      <li>• Peak activity occurs at <strong>{formatHour(analytics.inferredMetrics.mostActiveHour)}</strong></li>
                      <li>• <strong>{analytics.topProducts[0]?.productName}</strong> is your most popular product</li>
                      <li>• Average of <strong>{analytics.inferredMetrics.avgInteractionsPerDay}</strong> interactions per day</li>
                    </ul>
                  </div>
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">Recommendations</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>• Focus marketing efforts on <strong>{analytics.inferredMetrics.topPerformingLocation}</strong></li>
                      <li>• Schedule maintenance during off-peak hours</li>
                      <li>• Consider expanding <strong>{analytics.topProducts[0]?.productName}</strong> to more machines</li>
                      <li>• Monitor low-performing machines for optimization opportunities</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}







